import React, { useEffect, useState } from 'react';
import {
  Button, Col, DatePicker, Form, Input, InputNumber, Modal,
  Popconfirm, Row, Select, Space, Table, Tabs, Tag, Typography, message,
} from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  WarehouseItem, getWarehouseItems, createWarehouseItem, updateWarehouseItem, deleteWarehouseItem,
} from '../api/warehouse-items';
import {
  WarehouseReceipt, getReceipts, createReceipt, deleteReceipt,
} from '../api/warehouse-receipts';

const { Title } = Typography;
const LOW_STOCK_THRESHOLD = 3;

const WarehousePage: React.FC = () => {
  const [items, setItems] = useState<WarehouseItem[]>([]);
  const [receipts, setReceipts] = useState<WarehouseReceipt[]>([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [loadingReceipts, setLoadingReceipts] = useState(false);

  // Модалка позиции
  const [itemModal, setItemModal] = useState(false);
  const [editingItem, setEditingItem] = useState<WarehouseItem | null>(null);
  const [itemForm] = Form.useForm();

  // Модалка прихода
  const [receiptModal, setReceiptModal] = useState(false);
  const [receiptForm] = Form.useForm();

  const loadItems = async () => {
    setLoadingItems(true);
    try { setItems(await getWarehouseItems()); }
    finally { setLoadingItems(false); }
  };

  const loadReceipts = async () => {
    setLoadingReceipts(true);
    try { setReceipts(await getReceipts()); }
    finally { setLoadingReceipts(false); }
  };

  useEffect(() => { loadItems(); loadReceipts(); }, []);

  // ── Позиции ──────────────────────────────────────────────────────────────

  const openCreateItem = () => {
    setEditingItem(null);
    itemForm.resetFields();
    setItemModal(true);
  };

  const openEditItem = (item: WarehouseItem) => {
    setEditingItem(item);
    itemForm.setFieldsValue({ name: item.name, unit: item.unit, notes: item.notes });
    setItemModal(true);
  };

  const handleItemSubmit = async (values: any) => {
    try {
      if (editingItem) {
        const updated = await updateWarehouseItem(editingItem.id, values);
        setItems(prev => prev.map(i => i.id === updated.id ? updated : i));
        message.success('Обновлено');
      } else {
        const created = await createWarehouseItem(values);
        setItems(prev => [...prev, created]);
        message.success('Добавлено');
      }
      setItemModal(false);
    } catch (e: any) {
      message.error(e?.response?.data?.detail || 'Ошибка');
    }
  };

  const handleDeleteItem = async (id: number) => {
    try {
      await deleteWarehouseItem(id);
      setItems(prev => prev.filter(i => i.id !== id));
      message.success('Удалено');
    } catch (e: any) {
      message.error(e?.response?.data?.detail || 'Нельзя удалить: есть операции');
    }
  };

  const itemColumns = [
    {
      title: 'Наименование', dataIndex: 'name', key: 'name',
      render: (v: string, r: WarehouseItem) => (
        <a onClick={() => openEditItem(r)}>{v}</a>
      ),
    },
    { title: 'Единица', dataIndex: 'unit', key: 'unit', width: 80 },
    {
      title: 'Остаток', dataIndex: 'current_stock', key: 'stock', width: 100,
      render: (v: number, r: WarehouseItem) => (
        <Tag color={v < LOW_STOCK_THRESHOLD ? 'red' : 'green'}>
          {v} {r.unit}
        </Tag>
      ),
    },
    { title: 'Заметки', dataIndex: 'notes', key: 'notes', render: (v: string | null) => v ?? '—' },
    {
      title: '', key: 'del', width: 50,
      render: (_: any, r: WarehouseItem) => (
        <Popconfirm title="Удалить позицию?" onConfirm={() => handleDeleteItem(r.id)} okText="Да" cancelText="Нет">
          <Button size="small" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      ),
    },
  ];

  // ── Приходы ──────────────────────────────────────────────────────────────

  const openReceiptModal = () => {
    receiptForm.resetFields();
    receiptForm.setFieldsValue({ date: dayjs(), lines: [{}] });
    setReceiptModal(true);
  };

  const handleReceiptSubmit = async (values: any) => {
    try {
      const lines = (values.lines || []).filter((l: any) => l?.warehouse_item_id);
      if (lines.length === 0) {
        message.error('Добавьте хотя бы одну позицию');
        return;
      }
      const created = await createReceipt({
        date: values.date.format('YYYY-MM-DD'),
        supplier: values.supplier,
        notes: values.notes,
        items: lines.map((l: any) => ({
          warehouse_item_id: l.warehouse_item_id,
          quantity: l.quantity,
          price_per_unit: l.price_per_unit,
        })),
      });
      setReceipts(prev => [created, ...prev]);
      // Обновить остатки
      await loadItems();
      setReceiptModal(false);
      message.success('Приход оформлен');
    } catch (e: any) {
      message.error(e?.response?.data?.detail || 'Ошибка');
    }
  };

  const handleDeleteReceipt = async (id: number) => {
    try {
      await deleteReceipt(id);
      setReceipts(prev => prev.filter(r => r.id !== id));
      message.success('Удалено');
    } catch {
      message.error('Ошибка');
    }
  };

  const receiptColumns = [
    { title: 'Дата', dataIndex: 'date', key: 'date', width: 110 },
    { title: 'Поставщик', dataIndex: 'supplier', key: 'sup', render: (v: string | null) => v ?? '—' },
    {
      title: 'Позиций', key: 'cnt', width: 80,
      render: (_: any, r: WarehouseReceipt) => r.items.length,
    },
    {
      title: 'Сумма', key: 'sum', width: 110,
      render: (_: any, r: WarehouseReceipt) => {
        const total = r.items.reduce((acc, i) => acc + +i.quantity * +i.price_per_unit, 0);
        return `${total.toFixed(2)} ₽`;
      },
    },
    { title: 'Заметки', dataIndex: 'notes', key: 'notes', render: (v: string | null) => v ?? '—' },
    {
      title: '', key: 'del', width: 50,
      render: (_: any, r: WarehouseReceipt) => (
        <Popconfirm title="Удалить приход?" onConfirm={() => handleDeleteReceipt(r.id)} okText="Да" cancelText="Нет">
          <Button size="small" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Title level={4} style={{ marginBottom: 16 }}>Склад запчастей</Title>

      <Tabs
        items={[
          {
            key: 'items',
            label: 'Остатки',
            children: (
              <>
                <Space style={{ marginBottom: 12, justifyContent: 'flex-end', width: '100%' }}>
                  <Button type="primary" icon={<PlusOutlined />} onClick={openCreateItem}>
                    Добавить позицию
                  </Button>
                </Space>
                <Table
                  columns={itemColumns}
                  dataSource={items}
                  rowKey="id"
                  loading={loadingItems}
                  size="middle"
                  pagination={false}
                />
              </>
            ),
          },
          {
            key: 'receipts',
            label: 'Приходы',
            children: (
              <>
                <Space style={{ marginBottom: 12, justifyContent: 'flex-end', width: '100%' }}>
                  <Button type="primary" icon={<PlusOutlined />} onClick={openReceiptModal}>
                    Оформить приход
                  </Button>
                </Space>
                <Table
                  columns={receiptColumns}
                  dataSource={receipts}
                  rowKey="id"
                  loading={loadingReceipts}
                  size="middle"
                  pagination={{ pageSize: 20 }}
                />
              </>
            ),
          },
        ]}
      />

      {/* Модалка позиции */}
      <Modal
        title={editingItem ? 'Редактировать позицию' : 'Новая позиция склада'}
        open={itemModal}
        onCancel={() => setItemModal(false)}
        onOk={() => itemForm.submit()}
        okText="Сохранить"
        cancelText="Отмена"
      >
        <Form form={itemForm} layout="vertical" onFinish={handleItemSubmit}>
          <Form.Item name="name" label="Наименование" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="unit" label="Единица измерения" initialValue="шт">
            <Select options={[
              { value: 'шт', label: 'шт' },
              { value: 'л', label: 'л' },
              { value: 'кг', label: 'кг' },
              { value: 'м', label: 'м' },
            ]} />
          </Form.Item>
          <Form.Item name="notes" label="Заметки">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Модалка прихода */}
      <Modal
        title="Оформить приход"
        open={receiptModal}
        onCancel={() => setReceiptModal(false)}
        onOk={() => receiptForm.submit()}
        okText="Сохранить"
        cancelText="Отмена"
        width={680}
      >
        <Form form={receiptForm} layout="vertical" onFinish={handleReceiptSubmit}>
          <Row gutter={12}>
            <Col span={10}>
              <Form.Item name="date" label="Дата" rules={[{ required: true }]}>
                <DatePicker style={{ width: '100%' }} format="DD.MM.YYYY" />
              </Form.Item>
            </Col>
            <Col span={14}>
              <Form.Item name="supplier" label="Поставщик">
                <Input placeholder="Название магазина / поставщика" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="notes" label="Заметки">
            <Input />
          </Form.Item>

          <Typography.Text strong>Позиции:</Typography.Text>
          <Form.List name="lines">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name }) => (
                  <Space key={key} align="baseline" style={{ display: 'flex', marginTop: 8 }}>
                    <Form.Item name={[name, 'warehouse_item_id']} style={{ marginBottom: 0 }}>
                      <Select
                        placeholder="Позиция"
                        style={{ width: 200 }}
                        options={items.map(i => ({ value: i.id, label: i.name }))}
                      />
                    </Form.Item>
                    <Form.Item name={[name, 'quantity']} style={{ marginBottom: 0 }}>
                      <InputNumber placeholder="Кол-во" min={0.001} precision={3} style={{ width: 90 }} />
                    </Form.Item>
                    <Form.Item name={[name, 'price_per_unit']} style={{ marginBottom: 0 }}>
                      <InputNumber placeholder="Цена/ед ₽" min={0} precision={2} style={{ width: 110 }} />
                    </Form.Item>
                    <Button danger size="small" onClick={() => remove(name)}>✕</Button>
                  </Space>
                ))}
                <Button
                  type="dashed"
                  onClick={() => add()}
                  icon={<PlusOutlined />}
                  style={{ marginTop: 8 }}
                  size="small"
                >
                  Добавить строку
                </Button>
              </>
            )}
          </Form.List>
        </Form>
      </Modal>
    </div>
  );
};

export default WarehousePage;
